import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { LoadingButton } from '@mui/lab'
import { Stack, Radio, RadioGroup, FormControlLabel, Typography } from '@mui/material'
import { ImmutableVector3, getCenterMultipleReduceMean } from '@modugen/ui-toolkit'
import { useMutation } from '@tanstack/react-query'
import { SwitchLabeled } from '@ui/actions'
import { CoordinatePointInput, Errors, Form } from '@ui/forms'
import { SplitElementIcon } from '@ui/icons'
import { enqueueSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { CollapsibleSplitBoxSection } from '@editorComponents'
import { getModel, getElementLoads } from '@queries'
import { buildErrorMessage } from 'src/constants/errors'
import client from 'src/state/client'
import { getVerticalTransmissionGraph } from 'src/state/queries/tensileTransmission'
import { splitVerticalSlabLikeSchema } from './schema'
import {
  SplitData,
  splitVerticalSlabLikeParallelToSpan,
  splitVerticalSlabLikeOrthogonalToSpan,
} from './utils'

interface Props {
  element_guid: string
  setSlabStale: (element: ShapeObject) => void
  handleUpdate: (element: ShapeObject) => void
  handleCreate: (element: ShapeObject) => void
}

interface SplitFormValues {
  splitDirectionWRTSpan: 'parallel' | 'orthogonal'
  splitPoint: Point
  slabLike: ShapeObject
}

const SplitVerticalSlabLikeFormFields = () => {
  const { setValue, watch } = useFormContext()

  const [limitXYDecimals, setLimitXYDecimals] = useState(true)
  const [limitZDecimals, setLimitZDecimals] = useState(false)
  const pointManipulation = useMemo(
    () => ({
      x: { roundOnReplace: limitXYDecimals },
      y: { roundOnReplace: limitXYDecimals },
      z: { roundOnReplace: limitZDecimals },
    }),
    [limitXYDecimals, limitZDecimals],
  )

  return (
    <>
      <Stack spacing={1} alignContent={'center'}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          paddingY={0}
          paddingX={1}
        >
          <RadioGroup
            value={watch('splitDirectionWRTSpan')}
            onChange={event => {
              setValue('splitDirectionWRTSpan', event.target.value)
            }}
            aria-label="split direction"
            row
          >
            <FormControlLabel
              value="parallel"
              control={<Radio size="small" />}
              label="Parallel"
              labelPlacement="end"
            />
            <FormControlLabel
              value="orthogonal"
              control={<Radio size="small" />}
              label="Orthogonal"
              labelPlacement="end"
            />
          </RadioGroup>
          <Typography>zur Spannrichtung</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignContent="center">
          <CoordinatePointInput
            label="Teilungspunkt"
            name="splitPoint"
            pointManipulation={pointManipulation}
            actions={
              <>
                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                  <Typography>Auf 2 Dez. begrenzen:</Typography>
                  <SwitchLabeled
                    checked={limitXYDecimals}
                    onChange={event => {
                      setLimitXYDecimals(event.target.checked)
                    }}
                    label="XY"
                    sx={{ padding: 0 }}
                  />
                  <SwitchLabeled
                    checked={limitZDecimals}
                    onChange={event => {
                      setLimitZDecimals(event.target.checked)
                    }}
                    label="Z"
                    sx={{ padding: 0 }}
                  />
                </Stack>
              </>
            }
          />
          <LoadingButton type="submit" variant="contained" color="primary" size="small">
            <SplitElementIcon />
          </LoadingButton>
        </Stack>
      </Stack>
    </>
  )
}

const SplitVerticalSlabLikeForm = ({
  element_guid,
  setSlabStale,
  handleUpdate,
  handleCreate,
}: Props) => {
  const { projectId }: { projectId?: string } = useParams()
  const { vertical_slabs, vertical_roof_slabs } = useModelStore(state => state.model)
  const verticalSlabLikes = useMemo(() => {
    return [...vertical_slabs, ...vertical_roof_slabs]
  }, [vertical_slabs, vertical_roof_slabs])

  // memos
  const selectedElement = useMemo(() => {
    return verticalSlabLikes.find(el => el.guid === element_guid)
  }, [verticalSlabLikes, element_guid])

  const defaultValues = useMemo(
    () => ({
      splitDirectionWRTSpan: 'parallel' as 'parallel' | 'orthogonal',
      splitPoint: selectedElement
        ? getCenterMultipleReduceMean(selectedElement.shape.points)
        : { x: 0, y: 0, z: 0 },
      slabLike: selectedElement,
    }),
    [selectedElement],
  )

  const { mutate } = useMutation(
    async (formData: SplitFormValues) => {
      const splitPoint = new ImmutableVector3(
        formData.splitPoint.x,
        formData.splitPoint.y,
        formData.splitPoint.z,
      )
      const splitData =
        formData.splitDirectionWRTSpan === 'parallel'
          ? splitVerticalSlabLikeParallelToSpan(formData.slabLike as ShapeObject, splitPoint)
          : splitVerticalSlabLikeOrthogonalToSpan(formData.slabLike as ShapeObject, splitPoint)
      await handleUpdate(splitData.updatedPosition)
      await handleCreate(splitData.newPosition)
      return splitData
    },
    {
      onSuccess: async (data: SplitData) => {
        setSlabStale(data.updatedPosition)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        enqueueSnackbar('Decke erfolgreich geteilt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        const errorMessage = buildErrorMessage(error, 'Fehler beim Teilen der Decke')
        enqueueSnackbar(errorMessage, { variant: 'error' })
      },
    },
  )

  const handleSubmit = (values: SplitFormValues) => {
    mutate(values)
  }

  return (
    <>
      <CollapsibleSplitBoxSection label="Teilen" defaultExpanded={false}>
        <Form
          id={'SplitVerticalSlabLikeForm'}
          key={element_guid}
          defaultValues={defaultValues}
          validationSchema={splitVerticalSlabLikeSchema}
          onSubmit={handleSubmit}
        >
          <SplitVerticalSlabLikeFormFields />
          <Errors />
        </Form>
      </CollapsibleSplitBoxSection>
    </>
  )
}

export default SplitVerticalSlabLikeForm
